import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UpscalingRockFile, PhaseInfo } from '../../../core/models/upscaling-rock-files.model';
import { RockImagesService } from '../../../services/rock-images/rock-images.service';
import { RockThumbnailService } from '../../../services/rock-thumbnail/rock-thumbnail.service';

export interface ViewPhasesPopupData {
  fileName: string;
  rockFile?: UpscalingRockFile;
}

@Component({
  selector: 'app-view-phases-popup',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './view-phases-popup.component.html',
  styleUrl: './view-phases-popup.component.scss',
})
export class ViewPhasesPopupComponent implements OnInit, OnDestroy {
  thumbnailUrl: string | null = null;
  rockFile: UpscalingRockFile | null = null;
  phases: PhaseInfo[] = [];
  isLoadingThumbnail = true;
  isLoadingMetadata = true;
  thumbnailError = false;
  metadataError = false;
  fileName: string;

  constructor(
    public dialogRef: MatDialogRef<ViewPhasesPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ViewPhasesPopupData,
    private rockImagesService: RockImagesService,
    private rockThumbnailService: RockThumbnailService,
  ) {
    this.fileName = data.fileName;
    this.rockFile = data.rockFile || null;
  }

  ngOnInit(): void {
    this.loadThumbnail();
    if (!this.rockFile) {
      this.loadMetadata();
    } else {
      this.isLoadingMetadata = false;
      this.processPhases();
    }
  }

  ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockThumbnailService.revokeThumbnailUrl(this.thumbnailUrl);
    }
  }

  private loadThumbnail(): void {
    this.isLoadingThumbnail = true;
    this.thumbnailError = false;

    this.rockThumbnailService.getThumbnail(this.fileName, 'Upscaling').subscribe({
      next: (url: string | null) => {
        this.thumbnailUrl = url;
        this.thumbnailError = !url;
        this.isLoadingThumbnail = false;
      },
      error: () => {
        this.thumbnailError = true;
        this.isLoadingThumbnail = false;
      },
    });
  }

  private loadMetadata(): void {
    this.isLoadingMetadata = true;
    this.metadataError = false;

    this.rockImagesService.getRockFileMetadata(this.fileName, 'Upscaling').subscribe({
      next: (rockFile) => {
        this.rockFile = rockFile as UpscalingRockFile;
        this.isLoadingMetadata = false;
        this.processPhases();
      },
      error: () => {
        this.metadataError = true;
        this.isLoadingMetadata = false;
      },
    });
  }

  private processPhases(): void {
    if (this.rockFile && this.rockFile.phases) {
      this.phases = this.rockFile.phases;

      if (this.rockFile.normalizedPhases) {
        this.phases.forEach(phase => {
          const normalizedValue = this.rockFile!.normalizedPhases![phase.label.toString()];
          if (normalizedValue !== undefined) {
            phase.color = `rgb(${normalizedValue}, ${normalizedValue}, ${normalizedValue})`;
          } else {
            phase.color = 'rgb(128, 128, 128)';
          }
        });
      } else {
        const maxPhaseValue = this.rockFile.maxPhaseValue || 255;
        this.phases.forEach(phase => {
          const grayValue = Math.round((phase.label / maxPhaseValue) * 255);
          phase.color = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
        });
      }
    } else if (this.rockFile && this.rockFile.fractionHistogram) {
      this.phases = Object.entries(this.rockFile.fractionHistogram).map(([label, fraction], index) => {
        const labelNum = parseInt(label, 10);
        let grayValue = 128;

        if (this.rockFile!.normalizedPhases && this.rockFile!.normalizedPhases[label] !== undefined) {
          grayValue = this.rockFile!.normalizedPhases[label];
        } else if (this.rockFile!.maxPhaseValue) {
          grayValue = Math.round((labelNum / this.rockFile!.maxPhaseValue) * 255);
        }

        return {
          phase: index,
          label: labelNum,
          fraction: fraction,
          color: `rgb(${grayValue}, ${grayValue}, ${grayValue})`,
        };
      });
    }
  }

  get totalFraction(): number {
    return this.phases.reduce((sum, phase) => sum + phase.fraction, 0);
  }

  close(): void {
    this.dialogRef.close();
  }

  getFileNameWithoutExtension(): string {
    return this.fileName.replace(/\.[^/.]+$/, '');
  }
}
